import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { Card, Col, Row } from 'reactstrap';
import { SecuritizePage } from '@securitize/ui-components';
import Translate, { translate } from '@securitize/ui-components/i18n/Translate';
import Select from 'react-select';
import Cookies from 'js-cookie';
import { useMediaQuery } from 'react-responsive';
import * as _ from 'lodash';
import ContentCard from './components/ContentCard';
import DocumentsCard from './components/DocumentsCard';
import Calculator from '../../../../../components/Calculator';
import { getOpportunity, updateOpportunity } from './actions';
import './OpportunitiesDetailsPage.scss';
import CompleteVerificationModal from './components/CompleteVerificationModal';
import ProcessingVerificationModal from './components/ProcessingVerificationModal/ProcessingVerificationModal';
import { MixPanel } from '../../../../../utils/helpers/mixPanel';
import ExitProcess from '../ExitProcess';
import SignInvestmentModal from '../SignInvestmentModal';
import JurisdictionBlocked from './components/JurisdictionBlocked/JurisdictionBlocked';
import { DepositOptions } from '../../../../../utils/constants';

const getDimensions = (ele) => {
  if (!ele) return {};
  const { height } = ele.getBoundingClientRect();
  const { offsetTop } = ele;
  const offsetBottom = offsetTop + height;

  return {
    height,
    offsetTop,
    offsetBottom,
  };
};

const OpportunitiesDetailsPage = ({
  data,
  updateOpportunity,
  isError,
  isLoading,
  getOpportunity,
  user,
  issuerVariables,
}) => {
  const { search } = useLocation();
  const { id } = useParams();
  const { isProcessing } = queryString.parse(search) || {};
  const [calcData, setCalcData] = useState({});
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(
    user.verificationStatus === 'pending' && isProcessing === 'true',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const contents = (data.contents || []).sort((a, b) => a.order - b.order);
  const documents = (data.documents || []).sort((a, b) => a.order - b.order);
  const attributes = (data.attributes || []).sort((a, b) => a.order - b.order)
    .slice(0, 4);

  const {
    amount,
    currency,
  } = (data.investorInvestmentDetails || Cookies.getJSON(`${user.investorId}-${id}`) || {});
  const history = useHistory();

  const submitInvestment = () => {
    MixPanel.track('invest-click', { opportunityId: id, amount: calcData.investValue, currency: calcData.name });
    if (user.verificationStatus === 'pending') {
      Cookies.set(`${user.investorId}-${id}`, {
        amount: calcData.investValue,
        currency: calcData.name,
      });
      return setIsProcessingModalOpen(true);
    }
    if (user.verificationStatus !== 'verified') {
      Cookies.set(`${user.investorId}-${id}`, {
        amount: calcData.investValue,
        currency: calcData.name,
      });
      return setIsVerificationModalOpen(true);
    }

    setIsSubmitting(true);
    updateOpportunity(id, {
      amount: calcData.investValue,
      currency: calcData.name,
    }, {
      onFinish: (error) => {
        setIsSubmitting(false);
        setModalOpen(false);
        if (!error) {
          history.push({
            pathname: `/opportunity/${id}/investment`,
            search,
          });
        }
      },
    });
  };

  const onInvestClick = () => {
    if (data.hasSignedAgreement && (calcData.investValue !== amount || calcData.name !== currency)) {
      setModalOpen(true);
      return;
    }
    submitInvestment();
  };

  useEffect(() => {
    getOpportunity(id);
    setIsInitialized(true);
  }, []);

  const calculatorRef = useRef(null);
  const progressRef = useRef(null);
  const documentsRef = useRef(null);
  const headerRef = useRef(null);
  const contentsRef = useRef(null);

  const hideInvestmentProgress = issuerVariables['hide-investment-progress'] === 'true';

  const onlyCashAccountOption = data.depositOptions?.length === 1
    && data.depositOptions[0] === DepositOptions.CASH_ACCOUNT;

  const showJurisdictionBlocked = onlyCashAccountOption && !data.canUseCA;

  const investmentSteps = {
    ...(hideInvestmentProgress ? {} : {
      progress: {
        label: translate('Texts.progress'),
        value: 'progress',
        ref: progressRef,
        block: 'end',
      },
    }),
    documents: {
      label: translate('Texts.documents'),
      value: 'documents',
      ref: documentsRef,
      block: documents.length < 5 ? 'center' : 'end',
    },
    calculator: {
      label: translate('Texts.investment'),
      value: 'calculator',
      ref: calculatorRef,
      block: 'start',
    },
  };

  const [currentStep, setCurrentStep] = useState(investmentSteps.progress || investmentSteps.documents);

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const CalculatorComponent = (
    <>
      <Calculator
        ref={calculatorRef}
        inputId="opportunity-calculator-input"
        onChange={setCalcData}
        initValue={amount}
        currencyList={data.currencies || []}
        initialCurrency={currency}
        isTokenCalculatorVisible={data.isTokenCalculatorVisible}
        isLoading={isSubmitting}
        onInvestClick={onInvestClick}
        tokenName={data.tokenName}
        tokenDecimals={data.tokenDecimals}
      />
      {
        !!Object.keys(calcData || {}).length
        && (
          <SignInvestmentModal
            isOpen={isModalOpen}
            onToggle={() => {
              setModalOpen(!isModalOpen);
            }}
            currency={calcData.name}
            isLoading={isSubmitting}
            onConfirm={submitInvestment}
            investValue={calcData.investValue}
          />
        )
      }
    </>
  );

  useEffect(() => {
    const handleScroll = _.debounce(() => {
      const { height: headerHeight } = getDimensions(headerRef.current);
      const scrollPosition = window.scrollY + headerHeight;

      const selected = Object.values(investmentSteps).find(({ value, ref }) => {
        const ele = ref.current;
        if (ele) {
          const { offsetBottom, offsetTop } = getDimensions(ele);
          if (value === investmentSteps.documents.value) {
            const { offsetBottom: contentsOffsetBottom, offsetTop: contentsOffsetTop } = getDimensions(contentsRef.current);
            return scrollPosition > offsetTop + contentsOffsetTop && scrollPosition < offsetBottom + contentsOffsetBottom;
          }
          return scrollPosition > offsetTop && scrollPosition < offsetBottom;
        }
        return false;
      });
      if (selected) setCurrentStep(selected);
      else setCurrentStep(investmentSteps.calculator);
    }, 100);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <SecuritizePage isError={isError} isLoading={isLoading || !isInitialized}>
      <CompleteVerificationModal
        isOpen={isVerificationModalOpen}
        onToggle={() => setIsVerificationModalOpen(!isVerificationModalOpen)}
      />
      <ProcessingVerificationModal
        onToggle={() => setIsProcessingModalOpen(false)}
        isOpen={isProcessingModalOpen}
      />
      <div className="opportunities-details-page">
        <div className="opportunities-details-page__header mb-3" ref={headerRef}>
          <div className="mb-5 mb-lg-3">
            <ExitProcess />
          </div>
          <div className="font-32 font-weight-semi">
            <Translate translationKey="Texts.invest-in" parameters={{ token: data.title }} />
          </div>
          <Select
            arrowRenderer={({ isOpen }) => (
              <div className="custom-arrow-container">
                <vl is="separator" />
                <i className={!isOpen ? 'icon-point-down' : 'icon-point-up'} />
              </div>
            )}
            className="opportunities-details-page__step-select"
            onChange={({ value }) => {
              setCurrentStep(investmentSteps[value]);
              investmentSteps[value].ref.current.scrollIntoView({
                behavior: 'smooth',
                block: investmentSteps[value].block,
              });
            }}
            value={currentStep}
            options={Object.values(investmentSteps)}
            clearable={false}
            searchable={false}
          />
        </div>
        <Row className="opportunities-details-page__container">
          <Col lg={12}>
            <div className="opportunities-details-page__chips-container">
              {attributes.map((attr) => (
                <div key={attr.title + attr.content} className="opportunities-details-page__chip">
                  <span>
                    {attr.content}
                    &nbsp;-&nbsp;
                    {attr.title}
                  </span>
                </div>
              ))}
            </div>
          </Col>
          <Col className="opportunities-details-page__contents-column" lg={7} sm={12}>
            <Card
              className="opportunities-details-page__contents-column__contents-card border-0 box-shadow mt-3 mt-lg-4"
              innerRef={contentsRef}
            >
              {contents.map((content) => <ContentCard key={content.id} content={content} />)}
            </Card>
            {isMobile && CalculatorComponent}
          </Col>
          <Col lg={5} sm={12}>
            {showJurisdictionBlocked && <JurisdictionBlocked />}
            {!isMobile && CalculatorComponent}
            {!hideInvestmentProgress
            && (
            <Card
              className="opportunities-details-page__investment-progress-card p-3 p-xl-4 border-0 box-shadow"
              innerRef={progressRef}
            >
              <Row className="opportunities-details-page__investment-progress-card__title">
                <Col>
                  <i className="icon icon-coins mr-3" />
                  <Translate translationKey="Texts.progress" />
                </Col>
              </Row>
              <Row className="opportunities-details-page__investment-progress-card__amounts">
                <Col md={12} className="opportunities-details-page__investment-progress-card__amounts">
                  <p
                    className="opportunities-details-page__investment-progress-card__amounts__value issuer-primary"
                  >
                    $
                    {parseInt((data.amountRaised || 0)).toLocaleString()}
                        &nbsp;
                    <span className="opportunities-details-page__investment-progress-card__amounts__description">
                      <Translate translationKey="Texts.raised" />
                    </span>
                  </p>
                  <hr className="opportunities-details-page__investment-progress-card__amounts__divider issuer-primary" />
                  <p className="opportunities-details-page__investment-progress-card__amounts__value">
                    {parseInt((data.totalInvestorsFunded || 0)).toLocaleString()}
                        &nbsp;
                    <span
                      className="opportunities-details-page__investment-progress-card__amounts__description"
                    >
                      <Translate translationKey="Texts.investors" />
                    </span>
                  </p>
                </Col>
              </Row>
            </Card>
            )}
            {
             documents.length > 0
             && (
             <DocumentsCard
               ref={documentsRef}
               documents={documents}
               className={hideInvestmentProgress && 'top-spacing'}
               fullWidth={false}
             />
             )
            }
          </Col>
        </Row>
      </div>
    </SecuritizePage>
  );
};

OpportunitiesDetailsPage.defaultProps = {
  data: {},
  user: {},
  updateOpportunity: () => undefined,
  getOpportunity: () => undefined,
  isLoading: true,
  isError: false,
  issuerConfig: {
    securitizeIdUrl: '',
    issuerName: '',
  },
  issuerVariables: {},
};

OpportunitiesDetailsPage.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  user: PropTypes.objectOf(PropTypes.any),
  updateOpportunity: PropTypes.func,
  getOpportunity: PropTypes.func,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  issuerConfig: PropTypes.shape({
    securitizeIdUrl: PropTypes.string,
    issuerName: PropTypes.string,
    issuerId: PropTypes.string,
  }),
  issuerVariables: PropTypes.objectOf(PropTypes.any),
};

const mapStateToProps = (state) => {
  const {
    investment: { opportunity },
    auth: { user },
    issuerConfiguration,
  } = state;
  return {
    data: opportunity.data,
    isLoading: opportunity.isLoading,
    isError: opportunity.isError,
    user,
    issuerVariables: issuerConfiguration.variables,
  };
};

export default connect(mapStateToProps, {
  updateOpportunity,
  getOpportunity,
})(OpportunitiesDetailsPage);
